import React, { useState } from 'react';
import ReactDOM from 'react-dom';

/**
 * Shown when user taps Login but is still within the 6h relogin window after logout.
 */
const WelcomeBackModal = ({
  userName = 'there',
  gapLabel,
  logoutTimeLabel,
  onClose,
  onRelogin,
}) => {
  const [reloggingIn, setReloggingIn] = useState(false);
  const [error, setError] = useState('');

  const handleRelogin = async () => {
    if (reloggingIn || !onRelogin) return;
    setReloggingIn(true);
    setError('');
    try {
      await onRelogin();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to relogin');
    } finally {
      setReloggingIn(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-5 bg-black/55 backdrop-blur-[2px]">
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl shadow-2xl border border-emerald-200/70 dark:border-emerald-800 bg-white dark:bg-gray-800"
        role="dialog"
        aria-labelledby="welcome-back-title"
      >
        <div className="relative px-5 sm:px-6 pt-5 pb-4 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 text-white">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-white/20 transition-colors z-10"
            aria-label="Close"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <p className="text-emerald-50/90 text-xs sm:text-sm font-medium">Back already?</p>
          <h2 id="welcome-back-title" className="text-2xl font-bold tracking-tight mt-1 pr-8">
            Welcome back, {userName}
          </h2>
        </div>

        <div className="px-5 sm:px-6 py-5 space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            You are back early after{' '}
            <span className="font-bold text-emerald-700 dark:text-emerald-300 tabular-nums">
              {gapLabel || 'a short break'}
            </span>
            . I’ll relogin you for work.
          </p>
          {logoutTimeLabel && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Last logout · {logoutTimeLabel}
            </p>
          )}
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 pb-5 flex gap-2.5">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 text-sm font-semibold py-3 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleRelogin}
            disabled={reloggingIn}
            className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold py-3 transition-colors shadow-sm disabled:opacity-70"
          >
            {reloggingIn ? 'Relogging in…' : 'Relogin'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default WelcomeBackModal;
